
import {
  computed,
  defineComponent,
  onMounted,
  reactive,
  toRefs,
  getCurrentInstance,
  ref,
  watch
} from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "@/store";
import { api } from "@/apis/user";
import { getLoginName } from "@/utils/cookies";
export default defineComponent({
  setup() {
    const { t } = useI18n();
    const store = useStore();
    const { proxy } = getCurrentInstance() as any;
    const day = proxy.day;

    /** 系统管理员、总管理可看「换绑统计」Tab；三级仅「换绑详情」 */
    const canSeeStatsTab = computed(() => {
      const roles = store.state.user.roles || [];
      return (
        roles.includes("系统管理员") ||
        roles.includes("总管理")
      );
    });
    const multipleSelection = ref();
    const activeTab = ref<"detail" | "stats">("detail");
    const statsLoading = ref(false);
    const statsList = ref<any[]>([]);
    const statsTotal = ref(0);
    const statsPager = reactive({
      pageNo: 1,
      pageSize: 10
    });
    /** 统计月份（界面为月份选择器）；传参 ym 为 yyyy-MM-dd，固定为该月 1 号 */
    const statsYm = ref<string | null>(null);
    /** 下钻路径：每项为点击行的 loginName + subAdminId；请求 adminId 为最后一项 */
    const statsDrillSegments = ref<{ loginName: string; adminId: string }[]>([]);

    const statsBreadcrumbCrumbs = computed(() => {
      const rootName =
        String(getLoginName() || "").trim() ||
        String(store.state.user.name || "").trim() ||
        "—";
      const tail = statsDrillSegments.value.map((s) => ({
        loginName: String(s.loginName || "").trim() || "—",
        adminId: String(s.adminId || "").trim()
      }));
      return [{ loginName: rootName, adminId: null as string | null }, ...tail];
    });

    const statsDrillDepth = computed(() => statsDrillSegments.value.length);

    const fetchImeiLockStatistics = async () => {
      if (!canSeeStatsTab.value) {
        return;
      }
      statsLoading.value = true;
      try {
        const params: Record<string, unknown> = {
          pageNo: statsPager.pageNo,
          pageSize: statsPager.pageSize
        };
        const ymDay = toYmFirstDayString(statsYm.value);
        if (ymDay) {
          params.ym = ymDay;
        }
        const segs = statsDrillSegments.value;
        const aid =
          segs.length > 0 ? String(segs[segs.length - 1].adminId || "").trim() : "";
        if (aid) {
          params.adminId = aid;
        }
        const res: any = await api.list.imeiLockStatistics(params);
        const inner = res?.data?.data;
        const listRaw = inner?.resultList ?? inner?.records;
        statsList.value = Array.isArray(listRaw) ? listRaw : [];
        statsTotal.value =
          inner?.totalCount ?? inner?.total ?? 0;
      } catch (e) {
        console.log("/api/imei/lock/statistics 请求异常", e);
        statsList.value = [];
        statsTotal.value = 0;
      } finally {
        statsLoading.value = false;
      }
    };

    const handleStatsSizeChange = (val: number) => {
      statsPager.pageNo = 1;
      statsPager.pageSize = val;
      void fetchImeiLockStatistics();
    };

    const handleStatsCurrentChange = (page: number) => {
      statsPager.pageNo = page;
      void fetchImeiLockStatistics();
    };

    const onStatsYmChange = () => {
      statsPager.pageNo = 1;
      void fetchImeiLockStatistics();
    };

    const clearStatsAdminFilter = () => {
      statsDrillSegments.value = [];
      statsPager.pageNo = 1;
      void fetchImeiLockStatistics();
    };

    /** 统计表头面包屑：点击第 k 段（含根 k=0）回到该层级 */
    const onStatsBreadcrumbClick = (k: number) => {
      if (k >= statsBreadcrumbCrumbs.value.length - 1) {
        return;
      }
      statsDrillSegments.value = statsDrillSegments.value.slice(0, k);
      statsPager.pageNo = 1;
      void fetchImeiLockStatistics();
    };
    /** [startDate, endDate]；提交接口仅传 yyyy-MM-dd，不带时分秒 */
    const dateRange = ref<[string, string] | Date[] | null>(null);
    /** 详情按 adminId 筛选时，展示用管理员 loginName（仅传 adminId 请求，不传 account） */
    const detailFilterLoginName = ref<string | null>(null);

    /** 统一为本地日历 yyyy-MM-dd（不含时间），绝不把原始 Date 直接放进 params */
    const toDateOnlyString = (v: unknown): string | undefined => {
      if (v == null || v === "") {
        return undefined;
      }
      if (typeof v === "string") {
        const s = v.trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
          return s;
        }
        const parsed = day(s);
        return parsed.isValid() ? parsed.format("YYYY-MM-DD") : undefined;
      }
      if (v instanceof Date) {
        if (Number.isNaN(v.getTime())) {
          return undefined;
        }
        const y = v.getFullYear();
        const m = String(v.getMonth() + 1).padStart(2, "0");
        const d = String(v.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
      }
      return undefined;
    };

    /** 统计月份：界面选月，传参为该月 1 日 yyyy-MM-dd，不传 ISO/带时间的字符串 */
    const toYmFirstDayString = (v: unknown): string | undefined => {
      if (v == null || v === "") {
        return undefined;
      }
      if (typeof v === "string") {
        const s = v.trim();
        if (/^\d{4}-\d{2}$/.test(s)) {
          return `${s}-01`;
        }
        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
          const parsed = day(s);
          return parsed.isValid()
            ? parsed.startOf("month").format("YYYY-MM-DD")
            : undefined;
        }
        const parsed = day(s);
        return parsed.isValid()
          ? parsed.startOf("month").format("YYYY-MM-DD")
          : undefined;
      }
      if (v instanceof Date) {
        if (Number.isNaN(v.getTime())) {
          return undefined;
        }
        const y = v.getFullYear();
        const m = String(v.getMonth() + 1).padStart(2, "0");
        return `${y}-${m}-01`;
      }
      return undefined;
    };

    const formatCreateDate = (v: unknown) => {
      if (v == null || v === "") {
        return "—";
      }
      if (typeof v === "number") {
        const d = day(v);
        return d.isValid() ? d.format("YYYY-MM-DD HH:mm:ss") : "—";
      }
      const d = day(String(v));
      return d.isValid() ? d.format("YYYY-MM-DD HH:mm:ss") : String(v);
    };

    const dataMap = reactive({
      tableKey: 0,
      list: [] as any[],
      total: 0,
      name: null,
      listLoading: false,
      val: 10,
      listQuery: {
        page: {
          pageNo: 1,
          pageSize: 10
        },
        account: "",
        uid: "",
        imei: "",
        /** 从统计表 userType=4 跳转时按管理员 id 查详情 */
        adminId: ""
      },
      toSearch() {
        dataMap.getList(1, dataMap.listQuery.page.pageSize);
      },
      toReload() {
        dateRange.value = null;
        dataMap.listQuery.account = "";
        dataMap.listQuery.uid = "";
        dataMap.listQuery.imei = "";
        dataMap.listQuery.adminId = "";
        detailFilterLoginName.value = null;
        dataMap.getList(1, dataMap.listQuery.page.pageSize);
      },
      handleSelectionChange(val: any) {
        multipleSelection.value = val;
      },
      handleSizeChange(val: any) {
        dataMap.val = val;
        dataMap.getList(1, val);
      },
      handleCurrentChange(page: any) {
        dataMap.getList(page, dataMap.val);
      },

      async getList(page?: any, pageSize?: any, _userLoginEntity?: any) {
        if (page) {
          dataMap.listQuery.page.pageNo;
        }
        if (pageSize) {
          dataMap.listQuery.page.pageSize = pageSize;
        }
        dataMap.listQuery.page.pageNo = page;
        dataMap.listQuery.page.pageSize = pageSize;
        dataMap.listLoading = true;
        dataMap.listQuery.account = String(dataMap.listQuery.account ?? "").trim();
        dataMap.listQuery.uid = String(dataMap.listQuery.uid ?? "").trim();
        dataMap.listQuery.imei = String(dataMap.listQuery.imei ?? "").trim();
        const acc = dataMap.listQuery.account;
        const uidStr = dataMap.listQuery.uid;
        const imei = dataMap.listQuery.imei;
        const adminIdStr = String(dataMap.listQuery.adminId ?? "").trim();
        const params: Record<string, unknown> = {
          pageNo: dataMap.listQuery.page.pageNo,
          pageSize: dataMap.listQuery.page.pageSize
        };
        if (acc && !adminIdStr) {
          params.account = acc;
        }
        if (uidStr) {
          const uidNum = Number(uidStr);
          params.uid = Number.isNaN(uidNum) ? uidStr : uidNum;
        }
        if (imei) {
          params.imei = imei;
        }
        if (adminIdStr) {
          params.adminId = adminIdStr;
        }
        const dr = dateRange.value;
        if (dr && dr.length === 2) {
          const sd = toDateOnlyString(dr[0]);
          const ed = toDateOnlyString(dr[1]);
          if (sd && ed) {
            params.startDate = sd;
            params.endDate = ed;
          }
        }
        try {
          const res: any = await api.list.imeiLockRecordPage(params);
          const payload = res?.data;
          const inner = payload?.data;
          dataMap.list = Array.isArray(inner?.resultList)
            ? inner.resultList
            : [];
          dataMap.total =
            inner?.totalCount ?? inner?.total ?? 0;
        } catch (e) {
          console.log("/api/imei/lock/record/page 请求异常", e);
          dataMap.list = [];
          dataMap.total = 0;
        } finally {
          setTimeout(() => {
            dataMap.listLoading = false;
          }, 0.3 * 1000);
        }
      }
    });

    /** 仅总管理且无 adminId 时展示「仅本人换绑」说明；系统管理员、二级管理不展示；带 adminId 不展示 */
    const showDetailChiefOnlyHint = computed(() => {
      const roles = store.state.user.roles || [];
      if (roles.includes("系统管理员") || roles.includes("二级管理")) {
        return false;
      }
      if (!roles.includes("总管理")) {
        return false;
      }
      if (String(dataMap.listQuery.adminId || "").trim()) {
        return false;
      }
      return true;
    });

    /** userType=4：跳转换绑详情并按该管理员查记录；否则统计下钻 */
    const onStatsAdminNameClick = (row: any) => {
      const id = row?.subAdminId ?? row?.adminId;
      if (id == null || String(id).trim() === "") {
        return;
      }
      const ut = Number(row?.userType);
      if (ut === 4) {
        const ln = row?.loginName != null ? String(row.loginName).trim() : "";
        dateRange.value = null;
        dataMap.listQuery.uid = "";
        dataMap.listQuery.imei = "";
        dataMap.listQuery.account = "";
        dataMap.listQuery.adminId = String(id).trim();
        detailFilterLoginName.value = ln || null;
        dataMap.listQuery.page.pageNo = 1;
        activeTab.value = "detail";
        void dataMap.getList(1, dataMap.listQuery.page.pageSize);
        return;
      }
      const drillLn = row?.loginName != null ? String(row.loginName).trim() : "";
      statsDrillSegments.value = [
        ...statsDrillSegments.value,
        { loginName: drillLn || "—", adminId: String(id).trim() }
      ];
      statsPager.pageNo = 1;
      void fetchImeiLockStatistics();
    };

    const clearDetailAdminFilter = () => {
      dataMap.listQuery.adminId = "";
      detailFilterLoginName.value = null;
      dataMap.listQuery.page.pageNo = 1;
      void dataMap.getList(1, dataMap.listQuery.page.pageSize);
    };

    onMounted(() => {
      if (!canSeeStatsTab.value) {
        activeTab.value = "detail";
      }
      dataMap.getList(1, 10);
    });

    watch(canSeeStatsTab, (ok) => {
      if (!ok) {
        activeTab.value = "detail";
      }
    });

    watch(activeTab, (tab) => {
      if (tab === "stats" && canSeeStatsTab.value) {
        void fetchImeiLockStatistics();
      }
    });

    return {
      t,
      canSeeStatsTab,
      activeTab,
      statsLoading,
      statsList,
      statsTotal,
      statsPager,
      statsYm,
      statsBreadcrumbCrumbs,
      statsDrillDepth,
      onStatsBreadcrumbClick,
      handleStatsSizeChange,
      handleStatsCurrentChange,
      onStatsYmChange,
      onStatsAdminNameClick,
      clearStatsAdminFilter,
      clearDetailAdminFilter,
      detailFilterLoginName,
      showDetailChiefOnlyHint,
      dateRange,
      formatCreateDate,
      ...toRefs(dataMap)
    };
  }
});
